import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { forkJoin, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { MultiBreadcrumbEntry } from './breadcrumb.model';

import {
  AnalyticsService,
  CuringPoolssService,
  DevicesService,
  DocumentTemplatesService,
  GatewaysService,
  MeasurementsService,
  ProcessingJobsService,
  ProjectsService,
  StagesService,
  StrengthTestResultsService,
  ConcreteMixturesService,
  ConcreteModelsService,
  PoursService,
  LotsService,
  UsersService,
  BillingConfigService,
  SimulatorApiService
} from '@cosmos/angular/api';
import { TranslateService } from '@ngx-translate/core';
import { StateService } from '@cosmos/angular/utils';

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbsResolverService {
  temp = null;
  constructor(
    private projectsService: ProjectsService,
    private stagesService: StagesService,
    private measurementsService: MeasurementsService,
    private devicesService: DevicesService,
    private gatewaysService: DevicesService,
    private translateService: TranslateService,
    private curingPoolService: CuringPoolssService,
    private analyticsService: AnalyticsService,
    private processingJobsService: ProcessingJobsService,
    private strengthTestResultsService: StrengthTestResultsService,
    private documentTemplatesService: DocumentTemplatesService,
    private lotService: LotsService,
    private concreteMixturesService: ConcreteMixturesService,
    private concreteModelsService: ConcreteModelsService,
    private stateService: StateService,
    private poursService: PoursService,
    private usersService: UsersService,
    private billingConfigService: BillingConfigService,
    private simulatorApiService: SimulatorApiService,
  ) {}

  data = {
    project: this.projectsService,
    stage: this.stagesService,
    measurement: this.measurementsService,
    device: this.devicesService,
    gateway: this.gatewaysService,
    curingpool: this.curingPoolService,
    documentTemplate: this.documentTemplatesService,
    processingJob: this.processingJobsService,
    concreteMixture: this.concreteMixturesService,
    concreteModel: this.concreteModelsService,
    testResult: this.strengthTestResultsService,
    lot: this.lotService,
    pour: this.poursService,
    user: this.usersService,
    billingConfig: this.billingConfigService,
    runId: this.simulatorApiService
  };

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    const id = route.params[route.data.resolve];

    if (route.data.resolve === 'stage') {
      return this.resolveStageAncestorCrumbs(id, this.findProjectId(route));
    }

    if (route.data.resolve === 'measurement') {
      return this.resolveMeasurementCrumbs(id, this.findProjectId(route));
    }

    let type: string = '';
    let query = '?select=name';
    switch (route.data.resolve) {
      case 'project':
        type = 'breadcrumbs.project';
        this.projectsService.findById(id).subscribe({
          next: (response) =>
            this.stateService.setCurrentProject(response.body),
        });
        break;
      case 'gateway':
        type = 'breadcrumbs.gateway';
        break;
      case 'device':
        type = 'breadcrumbs.device';
        break;
      case 'curingpool':
        type = 'breadcrumbs.curing-pool';
        break;
      case 'documentTemplate':
        type = 'breadcrumbs.document-template';
        break;
      case 'concreteMixture':
        type = 'breadcrumbs.concrete';
        break;
      case 'concreteModel':
        type = 'breadcrumbs.concrete-model';
        break;
      case 'processingJob':
        type = 'breadcrumbs.processing-job';
        query = '?select=file&populate_file=filename';
        break;
      case 'testResult':
        type = 'breadcrumbs.test-result';
        query = '?select=sampleId';
        break;
      case 'lot':
        type = 'breadcrumbs.lot';
        query = '?select=code';
        break;
      case 'pour':
        type = 'breadcrumbs.pour';
        query = '?select=deliveryNoteId';
        break;
      case 'runId':
        type = 'breadcrumbs.simulation';
        query = '?select=elementType,simulationType,pourDate';
        break;
      case 'user':
        type = 'breadcrumbs.empty';
        query = '?select=name,surname';
        break;
      case 'billingConfig':
        type = 'breadcrumbs.billing-quota';
        query = '?populate_project=name&select=project';
        break;
      default:
        type = 'breadcrumbs.empty';
        break;
    }
    const prefix = type ? this.translateService.get(type) : '';
    const suffix = this.data[route.data.resolve].findById(id, query);
    return forkJoin([prefix, suffix]);
  }

  // Walks up the route tree to find the `:project` param — stage/measurement
  // routes are flat (`monitoring/stage/:stageId`), so the project id lives on
  // an ancestor segment, not on this route's own params.
  private findProjectId(route: ActivatedRouteSnapshot): string {
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
      if (current.params['project']) return current.params['project'];
      current = current.parent;
    }
    return '';
  }

  private resolveStageAncestorCrumbs(stageId: string, projectId: string): Observable<MultiBreadcrumbEntry[]> {
    return this.stagesService.getAncestors(stageId).pipe(
      switchMap((ancestorsRes) => {
        const chain = ancestorsRes.body ?? [];
        return this.translateService.get('breadcrumbs.stage').pipe(
          map((prefix) => chain.map((s) => ({
            label: `${prefix} ${s.name}`,
            url: `/projects/${projectId}/monitoring/stage/${s._id}`,
          }))),
        );
      }),
    );
  }

  private resolveMeasurementCrumbs(measurementId: string, projectId: string): Observable<MultiBreadcrumbEntry[]> {
    return this.measurementsService.findById(measurementId, '?select=name,stage&populate_stage=name').pipe(
      switchMap((measurementRes) => {
        const measurement = measurementRes.body as any;
        const stageId = measurement.stage?._id ?? measurement.stage;
        return this.stagesService.getAncestors(stageId).pipe(
          switchMap((ancestorsRes) => {
            const chain = ancestorsRes.body ?? [];
            return forkJoin([
              this.translateService.get('breadcrumbs.stage'),
              this.translateService.get('breadcrumbs.measurement'),
            ]).pipe(
              map(([stagePrefix, measurementPrefix]) => [
                ...chain.map((s) => ({
                  label: `${stagePrefix} ${s.name}`,
                  url: `/projects/${projectId}/monitoring/stage/${s._id}`,
                })),
                {
                  label: `${measurementPrefix} ${measurement.name}`,
                  url: `/projects/${projectId}/monitoring/measurement/${measurementId}`,
                },
              ]),
            );
          }),
        );
      }),
    );
  }
}
